import "./PollyQuestionTypeMenuInMeeting.scss";

import { Flex, Menu, MenuItemProps, ShorthandCollection, Tooltip } from "@fluentui/react-northstar";
import { QuestionType } from "@teams/types";
import { QuestionTemplateDTO } from "@teams/types/api";
import React, { useMemo } from "react";

import { PollyIcon } from "../../../_shared/components/PollyIcon";

export const PollyQuestionTypeMenuInMeeting: React.FC<{
  activeType: QuestionType;
  activeTemplate?: string;
  templates: QuestionTemplateDTO[];
  allowedTypes: QuestionType[];
  compressed?: boolean;
  onTypeSelected: (type: QuestionType, templateId?: string) => void;
  disableMenu?: boolean;
}> = ({
  activeType,
  activeTemplate,
  onTypeSelected,
  templates,
  allowedTypes,
  compressed,
  disableMenu,
}) => {
  const menuItems = useMemo(() => {
    const items: ShorthandCollection<MenuItemProps> = [];

    if (allowedTypes.includes("nominal")) {
      items.push({
        key: "multichoice",
        icon: (
          <Tooltip content="Multiple choice">
            <PollyIcon icon="multiple-choice-icon" inheritColor />
          </Tooltip>
        ),
        content: compressed ? undefined : "Choice",
        active: activeType === "nominal",
        onClick: () => onTypeSelected("nominal"),
        ...(disableMenu && { disabled: true }),
      });
    }

    if (allowedTypes.includes("open-ended")) {
      items.push({
        key: "openended",
        icon: (
          <Tooltip content="Open ended">
            <PollyIcon icon="open-text-icon" inheritColor />
          </Tooltip>
        ),
        content: compressed ? undefined : "Open ended",
        active: activeType === "open-ended",
        onClick: () => onTypeSelected("open-ended"),
        ...(disableMenu && { disabled: true }),
      });
    }

    if (allowedTypes.includes("ordinal")) {
      const ratingsItem: MenuItemProps & { key: string } = {
        key: "rating",
        icon:
          activeTemplate && !compressed ? undefined : (
            <Tooltip content="Rating">
              <PollyIcon icon="rating-icon" inheritColor />
            </Tooltip>
          ),
        content:
          activeTemplate && !compressed
            ? templates.find(t => t.id === activeTemplate)?.label
            : compressed
            ? undefined
            : "Rating",
        menu: {
          items: templates
            .filter(t => t.type === "ordinal")
            .map(template => ({
              key: template.id,
              content: template.label,
              onClick: () => onTypeSelected("ordinal", template.id),
            })),
        },
        ...(disableMenu && { disabled: true }),
      };

      if (activeType === "ordinal") {
        ratingsItem.active = true;
      }

      items.push(ratingsItem);
    }

    return items;
  }, [
    activeTemplate,
    activeType,
    onTypeSelected,
    templates,
    allowedTypes,
    compressed,
    disableMenu,
  ]);

  return (
    <Flex>
      <Menu className="polly-question-type-menu" primary items={menuItems} />
    </Flex>
  );
};
